import { Box, Paper, Skeleton, Tooltip, Typography } from "@leap4/frontend";
import React from "react";
import type { DashboardPacingDto, PacingMetricDto } from "../../../api/generated/models";
import { DASH, directionColor, formatMetric, signColor, valueFontSize } from "./format";

interface IProps {
    pacing: DashboardPacingDto | undefined;
    loading: boolean;
}

/** The change is always red for bad and green for good. Amber is reserved for the value. */
const changeColor = (m: PacingMetricDto): string => {
    if (m.pctChange === null || m.pctChange === undefined || !Number.isFinite(m.pctChange)) {
        return "text.secondary";
    }
    if (m.accent === "negative") return "error.main";
    if (m.accent === "positive") return "success.main";
    return "text.secondary";
};

/** The headline: how far up or down, as a percentage. */
const formatPct = (v: number | null | undefined): string => {
    if (v === null || v === undefined || !Number.isFinite(v)) return DASH;
    const pct = v * 100;
    const digits = Math.abs(pct) >= 10 ? 0 : 1;
    return `${pct > 0 ? "+" : ""}${pct.toFixed(digits)}%`;
};

const arrow = (v: number | null | undefined): string => {
    if (v === null || v === undefined || !Number.isFinite(v) || v === 0) return "";
    return v > 0 ? "▲" : "▼";
};

/** Why there is no percentage. Only one of these means a number is missing. */
const noChangeReason = (m: PacingMetricDto): string => {
    if (m.today === null || m.today === undefined) return "no data";
    if (m.yesterday === null || m.yesterday === undefined) return "no data for yesterday";
    if (m.today === 0 && m.yesterday === 0) return "none either day";
    if (m.yesterday === 0) return "none yesterday";
    return "no change vs yesterday";
};

/** Today's figure is the number you read; the change is context beside it, not the headline. */
const PacingCard = ({ m, hours }: { m: PacingMetricDto; hours: number }): JSX.Element => {
    const hasChange = m.pctChange !== null && m.pctChange !== undefined && Number.isFinite(m.pctChange);
    const text = formatMetric(m.today, m.unit, true);

    return (
        <Paper variant="outlined" sx={{ px: 1.75, py: 1.25, minWidth: 0, textAlign: "center" }}>
            {/* Both sides of the comparison, so "vs yesterday" never has to be taken on trust. */}
            <Tooltip
                placement="top"
                arrow
                title={
                    <>
                        <Box sx={{ fontWeight: 600 }}>{m.label}</Box>
                        {m.formula && <Box sx={{ mt: 0.5 }}>{m.formula}</Box>}
                        <Box sx={{ mt: 0.75 }}>
                            Today, first {hours}h: {formatMetric(m.today, m.unit, true)}
                        </Box>
                        <Box>
                            Yesterday, same first {hours}h: {formatMetric(m.yesterday, m.unit, true)}
                        </Box>
                    </>
                }
            >
                <Typography
                    variant="caption"
                    sx={{
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: 0.4,
                        fontWeight: 500,
                        display: "block",
                        whiteSpace: "normal",
                        lineHeight: 1.3,
                        minHeight: "2.6em",
                        cursor: "help",
                        textDecoration: "underline dotted",
                        textUnderlineOffset: 3,
                        textDecorationColor: "rgba(148,163,184,0.4)",
                    }}
                >
                    {m.label}
                </Typography>
            </Tooltip>

            {/* The value owns its row; the change shares the caption line below it. */}
            <Typography
                sx={{
                    mt: 0.25,
                    fontSize: valueFontSize(text),
                    fontWeight: 600,
                    lineHeight: 1.25,
                    fontVariantNumeric: "tabular-nums",
                    whiteSpace: "nowrap",
                    color: m.signed ? signColor(m.today) : directionColor(m.accent),
                }}
            >
                {text}
            </Typography>

            <Typography variant="caption" sx={{ display: "block", color: "text.disabled" }}>
                {hasChange ? (
                    <>
                        <Box component="span" sx={{ color: changeColor(m), fontWeight: 600 }}>
                            {arrow(m.pctChange)} {formatPct(m.pctChange)}
                        </Box>{" "}
                        vs yesterday
                    </>
                ) : (
                    noChangeReason(m)
                )}
            </Typography>
        </Paper>
    );
};

/** Today so far against the same slice of yesterday. */
const PacingStrip = ({ pacing, loading }: IProps): JSX.Element => {
    if (loading && !pacing) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.5,
                    "& > *": { flex: "1 1 160px", minWidth: 0 },
                }}
            >
                {Array.from({ length: 12 }).map((_, i) => (
                    <Skeleton key={i} variant="rounded" height={78} />
                ))}
            </Box>
        );
    }

    if (!pacing) return <></>;

    const cutoff = new Date(pacing.cutoffUtc);
    const clock = `${String(cutoff.getUTCHours()).padStart(2, "0")}:${String(
        cutoff.getUTCMinutes()
    ).padStart(2, "0")} UTC`;

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Typography variant="subtitle2">Today vs yesterday</Typography>
                <Tooltip
                    arrow
                    title={
                        "Both windows run from midnight UTC to the same time of day, and each counts only " +
                        "the conversions that had arrived by its own cut-off — so yesterday does not get " +
                        "an extra day of attribution maturity that today cannot have had. Revenue, Profit, " +
                        "CPA, ROAS, Margin and RPM are deliberately absent: spend is complete the moment " +
                        "an impression serves, but the conversions it earns arrive over the following days, " +
                        "so any ratio between them is meaningless this early. Those live in the window row " +
                        "below, over settled days."
                    }
                >
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        first {pacing.hoursElapsed}h of each day, as of {clock}
                    </Typography>
                </Tooltip>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.5,
                    "& > *": { flex: "1 1 160px", minWidth: 0 },
                }}
            >
                {pacing.metrics.map((m) => (
                    <PacingCard key={m.key} m={m} hours={pacing.hoursElapsed} />
                ))}
            </Box>
        </Box>
    );
};

export default PacingStrip;
